import { useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { FileText, Plus } from "lucide-react";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { ErrorState } from "../../UI/ErrorState.tsx";
import { Pagination } from "../../UI/Pagination.tsx";
import { DebouncedSearchInput } from "../../UI/DebouncedSearchInput.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  BonificoOrderDTO,
  BonificoOrderStatus,
  getBonificoOrderErrorMessage,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import {
  hasActiveBonificoOrdersFilters,
  pickBonificoOrdersFilters,
  toBonificoOrdersExportApiParams,
} from "../../ProductService/BonificoOrder/BonificoOrdersListQuery.ts";
import { useBonificoOrdersPage } from "../../ProductService/BonificoOrder/useBonificoOrdersPage.ts";
import { useBonificoOrderStatusCounts } from "../../ProductService/BonificoOrder/useBonificoOrderStatusCounts.ts";
import { useSetBonificoOrderStatus } from "../../ProductService/BonificoOrder/useSetBonificoOrderStatus.ts";
import { useLastInvoicedValueDate } from "../../ProductService/BonificoOrder/useLastInvoicedValueDate.ts";
import { useBonificoOrderInvoicing } from "../../ProductService/BonificoOrder/useBonificoOrderInvoicing.ts";
import { MarkAsPaidModal } from "./MarkAsPaidModal.tsx";
import { IssueInvoiceModal } from "./IssueInvoiceModal.tsx";
import { BulkIssueInvoicesModal } from "./BulkIssueInvoicesModal.tsx";
import { ExportOrdersCsvModal } from "../OrdersList/ExportOrdersCsvModal.tsx";
import { ExportCsvButton } from "../../UI/ExportCsvButton.tsx";
import { SalesGateway } from "../SalesStatistics/SaleRecord.ts";
import { SalesStatisticsButton } from "../SalesStatistics/SalesStatisticsButton.tsx";
import { downloadCsvFile } from "../../UI/Utils/downloadCsvFile.ts";
import { useCreateProductServiceAdminAxiosInstance } from "../../Auth/useCreateProductServiceAdminAxiosInstance.ts";
import { EditBonificoBillingModal } from "./EditBonificoBillingModal.tsx";
import { GrantB2BCoursesModal } from "./GrantB2BCoursesModal.tsx";
import { RefundBonificoOrderModal } from "./RefundBonificoOrderModal.tsx";
import { BonificoOrderDetailsModal } from "./BonificoOrderDetailsModal.tsx";
import { CreateBonificoOrderScreenPath } from "./CreateBonificoOrderScreen.tsx";
import { toConfirmBonificoDraftPath } from "./confirmBonificoDraftPath.ts";
import { BonificoOrderActionHandlers } from "./BonificoOrderActions.tsx";
import { BonificoOrdersTable } from "./BonificoOrdersTable.tsx";
import { useBonificoOrdersListUrlState } from "./useBonificoOrdersListUrlState.ts";
import { BONIFICO_ORDERS_LIST_PATH } from "./bonificoOrdersListPath.ts";

export const AllBonificoOrdersScreenPath = BONIFICO_ORDERS_LIST_PATH;

function StatusShortcutChip({
  label,
  count,
  isActive,
  onToggle,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={classNames(
        "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
        isActive
          ? "border-primary bg-primary text-white"
          : "border-gray-300 bg-white text-gray-700 hover:border-gray-500",
      )}
    >
      {label}
      <span
        className={classNames(
          "min-w-6 rounded-full px-1.5 text-xs font-semibold",
          isActive ? "bg-white/25 text-white" : "bg-gray-100 text-gray-600",
        )}
      >
        {count}
      </span>
    </button>
  );
}

export function AllBonificoOrdersScreen() {
  const navigate = useNavigate();
  const { query, setSearch, setSort, setFilters, toPage } =
    useBonificoOrdersListUrlState();
  const filters = pickBonificoOrdersFilters(query);
  const [selectedOrder, setSelectedOrder] = useState<BonificoOrderDTO | null>(
    null,
  );
  const [markingOrder, setMarkingOrder] = useState<BonificoOrderDTO | null>(
    null,
  );
  const [grantingOrder, setGrantingOrder] = useState<BonificoOrderDTO | null>(
    null,
  );
  const [issuingOrder, setIssuingOrder] = useState<BonificoOrderDTO | null>(
    null,
  );
  const [isIssuingInvoicesInBulk, setIsIssuingInvoicesInBulk] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { createAxiosInstance } = useCreateProductServiceAdminAxiosInstance();
  const [refundingOrder, setRefundingOrder] = useState<BonificoOrderDTO | null>(
    null,
  );
  const [editingBillingOrder, setEditingBillingOrder] =
    useState<BonificoOrderDTO | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { orders, totalCount, isLoading, error, refetch } =
    useBonificoOrdersPage(query);
  const { countOf, refetch: refetchCounts } = useBonificoOrderStatusCounts();
  const { lastValueDateUtc, refetch: refetchLastInvoicedValueDate } =
    useLastInvoicedValueDate();
  const { setStatus, loading: settingStatus } = useSetBonificoOrderStatus();
  const { clearValueDate, loading: clearingValueDate } =
    useBonificoOrderInvoicing();
  const awaitingInvoiceCount = countOf(BonificoOrderStatus.AwaitingInvoice);
  const totalPages = Math.max(1, Math.ceil(totalCount / query.pageSize));

  const refreshOrders = async () => {
    await Promise.all([
      refetch(),
      refetchCounts(),
      refetchLastInvoicedValueDate(),
    ]);
  };

  const isOnlyStatus = (status: BonificoOrderStatus) =>
    query.statuses?.length === 1 && query.statuses[0] === status;
  const toggleOnlyStatus = (status: BonificoOrderStatus) =>
    setFilters({
      ...filters,
      statuses: isOnlyStatus(status) ? undefined : [status],
    });

  const updateOrder = async (update: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await update();
      setSelectedOrder(null);
      await refreshOrders();
    } catch (e) {
      setActionError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante l'aggiornamento dell'ordine. Riprova.",
        ),
      );
    }
  };

  const actionHandlers: BonificoOrderActionHandlers = {
    onConfirmDraft: (order) =>
      navigate(toConfirmBonificoDraftPath(order.orderId)),
    onMarkAsPaid: (order) => setMarkingOrder(order),
    onIssueInvoice: (order) => setIssuingOrder(order),
    onClearValueDate: (order) =>
      updateOrder(() => clearValueDate(order.orderId)),
    onRefund: (order) => setRefundingOrder(order),
    onGrantCourses: (order) => setGrantingOrder(order),
    onSetStatus: (order, status) =>
      updateOrder(() => setStatus(order.orderId, status)),
  };

  if (isLoading) return <FullPageLoading />;

  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className="flex-1 p-4 flex flex-col gap-4 max-w-full">
          <div className="flex items-center gap-3">
            <h1 className="font-bold text-3xl flex-1">Ordini Bonifico</h1>
            {awaitingInvoiceCount > 0 && (
              <button
                onClick={() => setIsIssuingInvoicesInBulk(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 shrink-0"
              >
                <FileText className="w-4 h-4" />
                Emetti fatture ({awaitingInvoiceCount})
              </button>
            )}
            <SalesStatisticsButton gateway={SalesGateway.Bonifico} />
            <ExportCsvButton onClick={() => setIsExporting(true)} />
            <button
              onClick={() => navigate(CreateBonificoOrderScreenPath)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 shrink-0"
            >
              <Plus className="w-4 h-4" />
              Nuovo ordine
            </button>
          </div>

          <DebouncedSearchInput
            placeholder="Cerca per cliente, ID ordine, importo, dati di fatturazione o note"
            value={query.search ?? ""}
            onSearch={setSearch}
          />

          <div className="flex flex-wrap items-center gap-2">
            <StatusShortcutChip
              label="Bozze da confermare"
              count={countOf(BonificoOrderStatus.Draft)}
              isActive={isOnlyStatus(BonificoOrderStatus.Draft)}
              onToggle={() => toggleOnlyStatus(BonificoOrderStatus.Draft)}
            />
            <StatusShortcutChip
              label="In attesa di pagamento"
              count={countOf(BonificoOrderStatus.PendingPayment)}
              isActive={isOnlyStatus(BonificoOrderStatus.PendingPayment)}
              onToggle={() =>
                toggleOnlyStatus(BonificoOrderStatus.PendingPayment)
              }
            />
            <StatusShortcutChip
              label="Fattura da emettere"
              count={awaitingInvoiceCount}
              isActive={isOnlyStatus(BonificoOrderStatus.AwaitingInvoice)}
              onToggle={() =>
                toggleOnlyStatus(BonificoOrderStatus.AwaitingInvoice)
              }
            />
          </div>

          {actionError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {actionError}
            </p>
          )}

          {error ? (
            <ErrorState />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                <span>{totalCount} ordini trovati</span>
                {hasActiveBonificoOrdersFilters(filters) && (
                  <button
                    type="button"
                    onClick={() => setFilters({})}
                    className="text-blue-600 hover:underline"
                  >
                    Rimuovi tutti i filtri
                  </button>
                )}
                <span className="ml-auto">
                  {lastValueDateUtc ? (
                    <>
                      Ultima fattura emessa (Bonifici Italia): data valuta{" "}
                      <strong className="text-gray-900">
                        {dayjs(lastValueDateUtc).format("DD/MM/YYYY")}
                      </strong>
                    </>
                  ) : (
                    "Nessuna fattura Bonifici Italia emessa finora"
                  )}
                </span>
              </div>
              <BonificoOrdersTable
                orders={orders}
                query={query}
                onSort={setSort}
                onFiltersChange={setFilters}
                onSelectOrder={setSelectedOrder}
                actionHandlers={actionHandlers}
                actionsDisabled={settingStatus || clearingValueDate}
              />
              <Pagination
                currentPage={query.page}
                totalPages={totalPages}
                onChangePage={toPage}
              />
            </>
          )}
        </div>
      </AdminMainContainer>
      <Footer />

      {selectedOrder && (
        <BonificoOrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          actionHandlers={{
            ...actionHandlers,
            onMarkAsPaid: (order) => {
              setSelectedOrder(null);
              setMarkingOrder(order);
            },
            onIssueInvoice: (order) => {
              setSelectedOrder(null);
              setIssuingOrder(order);
            },
            onRefund: (order) => {
              setSelectedOrder(null);
              setRefundingOrder(order);
            },
            onGrantCourses: (order) => {
              setSelectedOrder(null);
              setGrantingOrder(order);
            },
          }}
          onEditBilling={(order) => {
            setSelectedOrder(null);
            setEditingBillingOrder(order);
          }}
          onOrderUpdated={async (updated) => {
            setSelectedOrder(updated);
            await refreshOrders();
          }}
        />
      )}

      {editingBillingOrder && (
        <EditBonificoBillingModal
          order={editingBillingOrder}
          onSaved={async (updated) => {
            setEditingBillingOrder(null);
            setSelectedOrder(updated);
            await refreshOrders();
          }}
          onClose={() => {
            setSelectedOrder(editingBillingOrder);
            setEditingBillingOrder(null);
          }}
        />
      )}

      {grantingOrder && (
        <GrantB2BCoursesModal
          order={grantingOrder}
          onGranted={async () => {
            setGrantingOrder(null);
            await refreshOrders();
          }}
          onClose={() => setGrantingOrder(null)}
        />
      )}

      {markingOrder && (
        <MarkAsPaidModal
          order={markingOrder}
          onSuccess={async () => {
            setMarkingOrder(null);
            await refreshOrders();
          }}
          onClose={() => setMarkingOrder(null)}
          onOrderUpdated={refreshOrders}
        />
      )}

      {issuingOrder && (
        <IssueInvoiceModal
          order={issuingOrder}
          onSuccess={async () => {
            setIssuingOrder(null);
            await refreshOrders();
          }}
          onClose={() => setIssuingOrder(null)}
          onOrderUpdated={refreshOrders}
        />
      )}

      {isIssuingInvoicesInBulk && (
        <BulkIssueInvoicesModal
          onDone={async () => {
            setIsIssuingInvoicesInBulk(false);
            await refreshOrders();
          }}
          onClose={() => setIsIssuingInvoicesInBulk(false)}
        />
      )}

      {isExporting && (
        <ExportOrdersCsvModal
          totalCount={totalCount}
          search={query.search}
          hasFilters={hasActiveBonificoOrdersFilters(filters)}
          allOrdersDescription="tutti gli ordini bonifico"
          onExport={() =>
            downloadCsvFile(createAxiosInstance(), {
              url: "/bonifico-orders/export/csv",
              params: toBonificoOrdersExportApiParams(query),
              fileNamePrefix: "ordini-bonifico",
            })
          }
          onClose={() => setIsExporting(false)}
        />
      )}

      {refundingOrder && (
        <RefundBonificoOrderModal
          order={refundingOrder}
          onSuccess={async () => {
            setRefundingOrder(null);
            await refreshOrders();
          }}
          onClose={() => setRefundingOrder(null)}
        />
      )}
    </Layout>
  );
}
